<template>
  <div class="resolutions-page">
    <!-- Page Header -->
    <div class="d-flex flex-column flex-md-row justify-space-between align-center mb-6">
      <div class="mb-4 mb-md-0">
        <h1 class="text-h4 font-weight-bold text-primary">
          Board Resolutions
        </h1>
        <p class="text-subtitle-1 text-medium-emphasis">
          Manage board resolutions, voting, and track implementation status
        </p>
      </div>

      <div class="d-flex align-center gap-2">
        <v-btn
          color="secondary"
          variant="outlined"
          prepend-icon="mdi-chart-bar"
          @click="showVotingStats = true"
        >
          Voting Stats
        </v-btn>
        <v-btn
          color="primary"
          prepend-icon="mdi-plus"
          @click="$router.push('/resolutions/create')"
        >
          New Resolution
        </v-btn>
      </div>
    </div>

    <!-- Filters and Search -->
    <v-card class="mb-6" flat>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="searchQuery"
              prepend-inner-icon="mdi-magnify"
              label="Search resolutions..."
              variant="outlined"
              density="compact"
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-select
              v-model="statusFilter"
              :items="statusOptions"
              label="Status"
              variant="outlined"
              density="compact"
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-select
              v-model="typeFilter"
              :items="resolutionTypes"
              label="Type"
              variant="outlined"
              density="compact"
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-select
              v-model="votingStatusFilter"
              :items="votingStatusOptions"
              label="Voting Status"
              variant="outlined"
              density="compact"
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-btn
              color="primary"
              variant="outlined"
              block
              @click="clearFilters"
            >
              Clear Filters
            </v-btn>
          </v-col>
        </v-row>

        <!-- Quick Filter Chips -->
        <div class="mt-4">
          <v-chip-group
            v-model="quickFilter"
            color="primary"
            variant="outlined"
            multiple
            selected-class="text-primary"
          >
            <v-chip value="pending-vote">Pending My Vote</v-chip>
            <v-chip value="active-voting">Active Voting</v-chip>
            <v-chip value="recently-passed">Recently Passed</v-chip>
            <v-chip value="my-resolutions">My Resolutions</v-chip>
            <v-chip value="urgent">Urgent</v-chip>
          </v-chip-group>
        </div>
      </v-card-text>
    </v-card>

    <!-- Resolutions List -->
    <div class="resolutions-grid">
      <v-card
        v-for="resolution in filteredResolutions"
        :key="resolution.id"
        class="resolution-card mb-4"
        hover
        @click="viewResolution(resolution)"
      >
        <v-card-text class="pa-4">
          <div class="d-flex justify-space-between align-start mb-3">
            <div class="flex-grow-1">
              <h3 class="text-h6 font-weight-medium mb-2">
                {{ resolution.title }}
              </h3>
              <p class="text-body-2 text-medium-emphasis resolution-description">
                {{ resolution.description }}
              </p>
            </div>
            
            <div class="ml-4 d-flex gap-2 flex-wrap">
              <v-chip
                :color="getStatusColor(resolution.status)"
                size="small"
                variant="flat"
              >
                {{ resolution.status }}
              </v-chip>
              <v-chip
                v-if="resolution.status === 'voting' && isDeadlinePassed(resolution)"
                color="error"
                size="small"
                variant="flat"
                prepend-icon="mdi-clock-alert"
              >
                Deadline Expired
              </v-chip>
              <v-chip
                v-if="resolution.priority && resolution.priority !== 'Medium'"
                :color="getPriorityColor(resolution.priority)"
                size="small"
                variant="outlined"
              >
                {{ resolution.priority }}
              </v-chip>
            </div>
          </div>

          <!-- Resolution Meta Info -->
          <div class="resolution-meta mb-4">
            <div class="d-flex align-center mb-2">
              <v-icon size="small" class="mr-2">mdi-calendar</v-icon>
              <span class="text-caption">
                Created: {{ formatDate(resolution.created_at) }}
              </span>
              <v-spacer />
              <v-icon size="small" class="mr-2">mdi-account</v-icon>
              <span class="text-caption">
                By: {{ resolution.creator?.full_name || 'Unknown' }}
              </span>
            </div>
            
            <div v-if="resolution.meeting" class="d-flex align-center">
              <v-icon size="small" class="mr-2">mdi-calendar-clock</v-icon>
              <span class="text-caption">
                Meeting: {{ resolution.meeting.title }} - {{ formatDate(resolution.meeting.start_datetime) }}
              </span>
            </div>
          </div>

          <!-- Voting Section -->
          <div v-if="resolution.voting_enabled" class="voting-section">
            <div class="d-flex justify-space-between align-center mb-3">
              <h4 class="text-subtitle-2 font-weight-medium">Voting Status</h4>
              <v-chip
                :color="getVotingStatusColor(resolution.voting_status)"
                size="small"
                variant="flat"
              >
                {{ resolution.voting_status }}
              </v-chip>
            </div>

            <!-- Voting Progress -->
            <div class="voting-progress mb-3">
              <div class="d-flex justify-space-between align-center mb-2">
                <span class="text-caption">Progress</span>
                <span class="text-caption">
                  {{ resolution.votes_cast || 0 }} / {{ resolution.total_eligible_voters || 0 }} votes
                </span>
              </div>
              
              <v-progress-linear
                :model-value="getVotingProgress(resolution)"
                color="primary"
                height="6"
                rounded
              />
            </div>

            <!-- Vote Breakdown -->
            <div class="vote-breakdown mb-3">
              <v-row dense>
                <v-col cols="4">
                  <div class="text-center">
                    <div class="text-h6 text-success">{{ resolution.votes_for || 0 }}</div>
                    <div class="text-caption">For</div>
                  </div>
                </v-col>
                <v-col cols="4">
                  <div class="text-center">
                    <div class="text-h6 text-error">{{ resolution.votes_against || 0 }}</div>
                    <div class="text-caption">Against</div>
                  </div>
                </v-col>
                <v-col cols="4">
                  <div class="text-center">
                    <div class="text-h6 text-warning">{{ resolution.votes_abstain || 0 }}</div>
                    <div class="text-caption">Abstain</div>
                  </div>
                </v-col>
              </v-row>
            </div>

            <!-- User's Vote Status -->
            <div v-if="isAuthenticated" class="user-vote-section">
              <div v-if="getUserVote(resolution)" class="user-voted">
                <v-alert
                  :color="getVoteColor(getUserVote(resolution).vote_value)"
                  variant="tonal"
                  density="compact"
                  class="mb-2"
                >
                  <template #prepend>
                    <v-icon>mdi-check-circle</v-icon>
                  </template>
                  You voted: {{ getUserVote(resolution).vote_value?.toUpperCase() || 'N/A' }}
                  <span v-if="getUserVote(resolution).comments" class="ml-2">
                    - "{{ getUserVote(resolution).comments }}"
                  </span>
                </v-alert>
              </div>

              <div v-else-if="canVote(resolution)" class="voting-actions">
                <div class="d-flex gap-2">
                  <v-btn
                    color="success"
                    size="small"
                    variant="flat"
                    @click.stop="castVote(resolution, 'for')"
                    :loading="votingLoading[resolution.id]"
                  >
                    Vote For
                  </v-btn>
                  <v-btn
                    color="error"
                    size="small"
                    variant="flat"
                    @click.stop="castVote(resolution, 'against')"
                    :loading="votingLoading[resolution.id]"
                  >
                    Vote Against
                  </v-btn>
                  <v-btn
                    color="warning"
                    size="small"
                    variant="flat"
                    @click.stop="castVote(resolution, 'abstain')"
                    :loading="votingLoading[resolution.id]"
                  >
                    Abstain
                  </v-btn>
                </div>
              </div>

              <!-- Deadline passed but not yet finalised -->
              <div v-else-if="resolution.status === 'voting' && isDeadlinePassed(resolution)" class="deadline-passed">
                <v-alert
                  color="warning"
                  variant="tonal"
                  density="compact"
                  icon="mdi-clock-alert"
                  class="d-flex align-center"
                >
                  <div class="d-flex justify-space-between align-center w-100 flex-wrap gap-2">
                    <span>Voting period ended · finalising results…</span>
                    <v-btn
                      v-if="isAdmin"
                      size="x-small"
                      color="warning"
                      variant="flat"
                      :loading="closeVotingLoading[resolution.id]"
                      @click.stop="closeVoting(resolution)"
                      prepend-icon="mdi-gavel"
                    >
                      Close Now
                    </v-btn>
                  </div>
                </v-alert>
              </div>

              <div v-else-if="resolution.status !== 'voting'" class="voting-closed">
                <v-alert
                  color="grey"
                  variant="tonal"
                  density="compact"
                  icon="mdi-lock"
                >
                  Voting has closed
                </v-alert>
              </div>
            </div>

            <!-- Login Required Message -->
            <div v-else class="login-required">
              <v-alert
                color="info"
                variant="tonal"
                density="compact"
              >
                <template #prepend>
                  <v-icon>mdi-login</v-icon>
                </template>
                Please log in to participate in voting
                <template #append>
                  <v-btn
                    color="primary"
                    size="small"
                    variant="text"
                    @click="$router.push('/login')"
                  >
                    Login
                  </v-btn>
                </template>
              </v-alert>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="resolution-actions mt-4">
            <div class="d-flex justify-space-between align-center">
              <div class="d-flex gap-2">
                <v-btn
                  size="small"
                  variant="outlined"
                  @click.stop="viewResolution(resolution)"
                >
                  View Details
                </v-btn>
                <v-btn
                  v-if="resolution.documents && resolution.documents.length > 0"
                  size="small"
                  variant="outlined"
                  @click.stop="viewDocuments(resolution)"
                >
                  Documents ({{ resolution.documents.length }})
                </v-btn>
              </div>

              <v-menu>
                <template #activator="{ props }">
                  <v-btn
                    icon
                    size="small"
                    variant="text"
                    v-bind="props"
                    @click.stop
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item @click="editResolution(resolution)">
                    <v-list-item-title>Edit</v-list-item-title>
                    <template #prepend>
                      <v-icon>mdi-pencil</v-icon>
                    </template>
                  </v-list-item>
                  <v-list-item @click="duplicateResolution(resolution)">
                    <v-list-item-title>Duplicate</v-list-item-title>
                    <template #prepend>
                      <v-icon>mdi-content-copy</v-icon>
                    </template>
                  </v-list-item>
                  <v-list-item @click="shareResolution(resolution)">
                    <v-list-item-title>Share</v-list-item-title>
                    <template #prepend>
                      <v-icon>mdi-share</v-icon>
                    </template>
                  </v-list-item>
                  <v-list-item 
                    v-if="resolution.status === 'draft'"
                    @click="submitForVoting(resolution)"
                    class="text-success"
                  >
                    <v-list-item-title>Submit for Voting</v-list-item-title>
                    <template #prepend>
                      <v-icon>mdi-vote</v-icon>
                    </template>
                  </v-list-item>
                  <v-list-item
                    v-if="isAdmin && resolution.status === 'voting' && isDeadlinePassed(resolution)"
                    @click.stop="closeVoting(resolution)"
                    class="text-warning"
                  >
                    <v-list-item-title>Close Voting</v-list-item-title>
                    <template #prepend>
                      <v-icon>mdi-gavel</v-icon>
                    </template>
                  </v-list-item>
                  <v-divider />
                  <v-list-item @click="deleteResolution(resolution)" class="text-error">
                    <v-list-item-title>Delete</v-list-item-title>
                    <template #prepend>
                      <v-icon>mdi-delete</v-icon>
                    </template>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- Empty State -->
      <div v-if="filteredResolutions.length === 0 && !loading" class="empty-state text-center py-12">
        <v-icon size="96" color="grey">mdi-gavel</v-icon>
        <h3 class="text-h5 mt-4 mb-2">No Resolutions Found</h3>
        <p class="text-body-1 text-medium-emphasis mb-4">
          No resolutions match your current filters or search criteria.
        </p>
        <v-btn
          color="primary"
          @click="$router.push('/resolutions/create')"
        >
          Create First Resolution
        </v-btn>
      </div>
    </div>

    <!-- Vote Confirmation Dialog -->
    <v-dialog v-model="showVoteDialog" max-width="500">
      <v-card v-if="voteDialog.resolution">
        <v-card-title>
          Confirm Your Vote
        </v-card-title>
        <v-card-text>
          <h4 class="text-h6 mb-3">{{ voteDialog.resolution.title }}</h4>
          <p class="text-body-2 text-medium-emphasis mb-4">
            You are about to vote <strong>{{ voteDialog.voteType?.toUpperCase() || 'N/A' }}</strong> on this resolution.
          </p>
          
          <v-textarea
            v-model="voteDialog.comments"
            label="Comments (Optional)"
            variant="outlined"
            rows="3"
            hint="Provide any additional comments or reasoning for your vote"
            persistent-hint
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showVoteDialog = false">
            Cancel
          </v-btn>
          <v-btn
            :color="getVoteColor(voteDialog.voteType)"
            :loading="confirmingVote"
            @click="confirmVote"
          >
            Confirm Vote
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Voting Statistics Dialog -->
    <v-dialog v-model="showVotingStats" max-width="800" scrollable>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-h5">Voting Statistics</span>
          <v-btn
            icon
            variant="text"
            @click="showVotingStats = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <!-- Overall Stats -->
          <div class="stats-section mb-6">
            <h3 class="text-h6 mb-4">Overall Statistics</h3>
            <v-row>
              <v-col cols="6" md="3">
                <v-card class="text-center pa-3" color="primary" variant="tonal">
                  <div class="text-h4">{{ resolutions.length }}</div>
                  <div class="text-caption">Total Resolutions</div>
                </v-card>
              </v-col>
              <v-col cols="6" md="3">
                <v-card class="text-center pa-3" color="success" variant="tonal">
                  <div class="text-h4">{{ getResolutionsByStatus('passed').length }}</div>
                  <div class="text-caption">Passed</div>
                </v-card>
              </v-col>
              <v-col cols="6" md="3">
                <v-card class="text-center pa-3" color="error" variant="tonal">
                  <div class="text-h4">{{ getResolutionsByStatus('rejected').length }}</div>
                  <div class="text-caption">Rejected</div>
                </v-card>
              </v-col>
              <v-col cols="6" md="3">
                <v-card class="text-center pa-3" color="warning" variant="tonal">
                  <div class="text-h4">{{ getResolutionsByStatus('pending').length }}</div>
                  <div class="text-caption">Pending</div>
                </v-card>
              </v-col>
            </v-row>
          </div>

          <!-- Recent Voting Activity -->
          <div class="recent-activity">
            <h3 class="text-h6 mb-4">Recent Voting Activity</h3>
            <v-list>
              <v-list-item
                v-for="activity in recentVotingActivity"
                :key="activity.id"
                class="activity-item"
              >
                <template #prepend>
                  <v-avatar :color="getVoteColor(activity.vote)" size="32">
                    <v-icon color="white">
                      {{ getVoteIcon(activity.vote) }}
                    </v-icon>
                  </v-avatar>
                </template>
                
                <v-list-item-title>
                  {{ activity.user.full_name }} voted {{ activity.vote?.toUpperCase() || 'N/A' }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ activity.resolution.title }} • {{ formatDate(activity.created_at) }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showVotingStats = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">Delete Resolution</v-card-title>
        <v-card-text>
          Are you sure you want to delete "{{ selectedResolution?.title }}"? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn
            color="error"
            :loading="deleting"
            @click="confirmDelete"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Submit for Voting Confirmation Dialog -->
    <v-dialog v-model="showSubmitDialog" max-width="450">
      <v-card>
        <v-card-title class="text-h6">Submit Resolution for Voting</v-card-title>
        <v-card-text>
          <p>Are you sure you want to submit "{{ selectedResolution?.title }}" for voting?</p>
          <p class="text-body-2 text-medium-emphasis mt-2">
            Once submitted, the resolution status will change to "voting" and board members can cast their votes.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showSubmitDialog = false">Cancel</v-btn>
          <v-btn
            color="success"
            @click="confirmSubmitForVoting"
          >
            Submit for Voting
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { useAuthStore } from '@/store/auth'
import api from '@/services/api'

export default {
  name: 'Resolutions',
  setup() {
    const router = useRouter()
    const toast = useToast()
    const authStore = useAuthStore()

    // State
    const loading = ref(false)
    const resolutions = ref([])
    const searchQuery = ref('')
    const statusFilter = ref('')
    const typeFilter = ref('')
    const votingStatusFilter = ref('')
    const quickFilter = ref([])

    const isAuthenticated = computed(() => authStore.isAuthenticated)
    const currentUserId = computed(() => authStore.user?.id)
    const isAdmin = computed(() => authStore.user?.is_admin || false)

    // Dialog states
    const showVoteDialog = ref(false)
    const showVotingStats = ref(false)
    const showDeleteDialog = ref(false)
    const showSubmitDialog = ref(false)
    const selectedResolution = ref(null)

    // Voting
    const votingLoading = ref({})
    const closeVotingLoading = ref({})
    const confirmingVote = ref(false)
    const deleting = ref(false)
    const voteDialog = ref({
      resolution: null,
      voteType: '',
      comments: ''
    })

    // Statistics
    const recentVotingActivity = ref([])

    // Reference data
    const statusOptions = [
      'draft', 'pending', 'voting', 'passed', 'rejected', 'implemented', 'archived'
    ]

    const resolutionTypes = [
      'Standard', 'Emergency', 'Administrative', 'Financial', 'Strategic'
    ]

    const votingStatusOptions = [
      'not_started', 'open', 'closed'
    ]

    // Computed properties
    const filteredResolutions = computed(() => {
      let filtered = resolutions.value

      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        filtered = filtered.filter(resolution =>
          resolution.title.toLowerCase().includes(query) ||
          resolution.description.toLowerCase().includes(query)
        )
      }

      if (statusFilter.value) {
        filtered = filtered.filter(resolution => resolution.status === statusFilter.value)
      }

      if (typeFilter.value) {
        filtered = filtered.filter(resolution => resolution.type === typeFilter.value)
      }

      if (votingStatusFilter.value) {
        filtered = filtered.filter(resolution => resolution.voting_status === votingStatusFilter.value)
      }

      // Apply quick filters
      if (quickFilter.value.includes('pending-vote')) {
        filtered = filtered.filter(resolution => 
          canVote(resolution) && !getUserVote(resolution)
        )
      }

      if (quickFilter.value.includes('active-voting')) {
        filtered = filtered.filter(resolution => resolution.voting_status === 'open')
      }

      if (quickFilter.value.includes('recently-passed')) {
        const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
        filtered = filtered.filter(resolution => 
          resolution.status === 'passed' && new Date(resolution.updated_at) > weekAgo
        )
      }

      if (quickFilter.value.includes('my-resolutions')) {
        filtered = filtered.filter(resolution => 
          resolution.created_by === currentUserId.value
        )
      }

      if (quickFilter.value.includes('urgent')) {
        filtered = filtered.filter(resolution => resolution.priority === 'urgent')
      }

      return filtered
    })

    // Methods
    const clearFilters = () => {
      searchQuery.value = ''
      statusFilter.value = ''
      typeFilter.value = ''
      votingStatusFilter.value = ''
      quickFilter.value = []
    }

    const formatDate = (dateString) => {
      if (!dateString) return 'Not set'
      
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return 'Invalid Date'
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }

    const getStatusColor = (status) => {
      const colors = {
        draft: 'grey',
        pending: 'warning',
        voting: 'info',
        passed: 'success',
        rejected: 'error',
        implemented: 'purple',
        archived: 'grey-darken-2'
      }
      return colors[status] || 'grey'
    }

    const getPriorityColor = (priority) => {
      const colors = {
        Low: 'success',
        Medium: 'info',
        High: 'warning', 
        Urgent: 'error'
      }
      return colors[priority] || 'grey'
    }

    const getVotingStatusColor = (status) => {
      const colors = {
        not_started: 'grey',
        open: 'success',
        closed: 'error'
      }
      return colors[status] || 'grey'
    }

    const getVoteColor = (voteType) => {
      const colors = {
        for: 'success',
        against: 'error',
        abstain: 'warning'
      }
      return colors[voteType] || 'grey'
    }

    const getVoteIcon = (voteType) => {
      const icons = {
        for: 'mdi-thumb-up',
        against: 'mdi-thumb-down',
        abstain: 'mdi-minus'
      }
      return icons[voteType] || 'mdi-help'
    }

    const getVotingProgress = (resolution) => {
      if (!resolution.total_eligible_voters) return 0
      return (resolution.votes_cast / resolution.total_eligible_voters) * 100
    }

    const getUserVote = (resolution) => {
      return resolution.votes?.find(vote => vote.user_id === currentUserId.value)
    }

    const isDeadlinePassed = (resolution) => {
      if (!resolution.voting_deadline) return false
      return new Date() > new Date(resolution.voting_deadline)
    }

    const canVote = (resolution) => {
      return resolution.status === 'voting' &&
             !isDeadlinePassed(resolution) &&
             isAuthenticated.value &&
             !getUserVote(resolution)
    }

    const castVote = (resolution, voteType) => {
      if (!isAuthenticated.value) {
        toast.error('Please log in to vote')
        return
      }

      voteDialog.value = {
        resolution,
        voteType,
        comments: ''
      }
      showVoteDialog.value = true
    }

    const confirmVote = async () => {
      confirmingVote.value = true
      try {
        const response = await api.post(`/resolutions/${voteDialog.value.resolution.id}/vote`, {
          vote_value: voteDialog.value.voteType,
          comments: voteDialog.value.comments
        })

        // Update local data
        const resolution = resolutions.value.find(r => r.id === voteDialog.value.resolution.id)
        if (resolution) {
          if (!resolution.votes) resolution.votes = []
          resolution.votes.push(response.data.vote)
          
          // Update vote counts
          resolution.votes_cast = (resolution.votes_cast || 0) + 1
          if (voteDialog.value.voteType === 'for') {
            resolution.votes_for = (resolution.votes_for || 0) + 1
          } else if (voteDialog.value.voteType === 'against') {
            resolution.votes_against = (resolution.votes_against || 0) + 1
          } else if (voteDialog.value.voteType === 'abstain') {
            resolution.votes_abstain = (resolution.votes_abstain || 0) + 1
          }
        }

        toast.success('Vote cast successfully')
        showVoteDialog.value = false
      } catch (error) {
        console.error('Error casting vote:', error)
        toast.error('Failed to cast vote')
      } finally {
        confirmingVote.value = false
      }
    }

    const viewResolution = (resolution) => {
      router.push(`/resolutions/${resolution.id}`)
    }

    const editResolution = (resolution) => {
      router.push(`/resolutions/${resolution.id}/edit`)
    }

    const submitForVoting = (resolution) => {
      selectedResolution.value = resolution
      showSubmitDialog.value = true
    }

    const confirmSubmitForVoting = async () => {
      try {
        // Update resolution status to 'voting'
        const response = await api.put(`/resolutions/${selectedResolution.value.id}`, {
          status: 'voting'
        })

        // Update local data
        selectedResolution.value.status = 'voting'
        
        showSubmitDialog.value = false
        toast.success('Resolution submitted for voting successfully')
        
        // Reload resolutions to refresh the list
        loadResolutions()
      } catch (error) {
        console.error('Error submitting resolution for voting:', error)
        const message = error.response?.data?.message || 'Failed to submit resolution for voting'
        toast.error(message)
      }
    }

    const duplicateResolution = (resolution) => {
      router.push({
        path: '/resolutions/create',
        query: { duplicate: resolution.id }
      })
    }

    const shareResolution = (resolution) => {
      // Implement share functionality
      navigator.clipboard.writeText(window.location.origin + `/resolutions/${resolution.id}`)
      toast.success('Resolution link copied to clipboard')
    }

    const viewDocuments = (resolution) => {
      router.push(`/resolutions/${resolution.id}/documents`)
    }

    const deleteResolution = (resolution) => {
      selectedResolution.value = resolution
      showDeleteDialog.value = true
    }

    const confirmDelete = async () => {
      deleting.value = true
      try {
        await api.delete(`/resolutions/${selectedResolution.value.id}`)
        resolutions.value = resolutions.value.filter(r => r.id !== selectedResolution.value.id)
        toast.success('Resolution deleted successfully')
        showDeleteDialog.value = false
      } catch (error) {
        console.error('Error deleting resolution:', error)
        toast.error('Failed to delete resolution')
      } finally {
        deleting.value = false
      }
    }

    const getResolutionsByStatus = (status) => {
      return resolutions.value.filter(r => r.status === status)
    }

    // Data loading
    const loadResolutions = async () => {
      loading.value = true
      try {
        const response = await api.get('/resolutions/')
        resolutions.value = response.data.resolutions || []
      } catch (error) {
        console.error('Error loading resolutions:', error)
        toast.error('Failed to load resolutions')
      } finally {
        loading.value = false
      }
    }

    /**
     * Close voting on a resolution whose deadline has passed.
     * No `force` flag needed here — the backend accepts the request
     * from any authenticated user once the deadline is genuinely past.
     * Admins can also use this to finalise results via the "Close Now" button.
     */
    const closeVoting = async (resolution) => {
      closeVotingLoading.value[resolution.id] = true
      try {
        const response = await api.post(`/resolutions/${resolution.id}/close-voting`)
        // Replace the entry in the list with the updated resolution from the server
        const idx = resolutions.value.findIndex(r => r.id === resolution.id)
        if (idx !== -1) resolutions.value[idx] = response.data.resolution
        toast.success(response.data.message)
      } catch (error) {
        const msg = error.response?.data?.message || 'Failed to close voting'
        toast.error(msg)
      } finally {
        closeVotingLoading.value[resolution.id] = false
      }
    }

    const loadRecentVotingActivity = async () => {
      try {
        const response = await api.get('/resolutions/recent-activity')
        recentVotingActivity.value = response.data.activity || []
      } catch (error) {
        console.error('Error loading voting activity:', error)
      }
    }

    // Lifecycle
    onMounted(() => {
      loadResolutions()
      loadRecentVotingActivity()
    })

    return {
      // State
      loading,
      resolutions,
      searchQuery,
      statusFilter,
      typeFilter,
      votingStatusFilter,
      quickFilter,
      isAuthenticated,
      currentUserId,
      isAdmin,

      // Dialog states
      showVoteDialog,
      showVotingStats,
      showDeleteDialog,
      showSubmitDialog,
      selectedResolution,

      // Voting
      votingLoading,
      closeVotingLoading,
      confirmingVote,
      deleting,
      voteDialog,

      // Statistics
      recentVotingActivity,

      // Reference data
      statusOptions,
      resolutionTypes,
      votingStatusOptions,

      // Computed
      filteredResolutions,

      // Methods
      clearFilters,
      formatDate,
      getStatusColor,
      getPriorityColor,
      getVotingStatusColor,
      getVoteColor,
      getVoteIcon,
      getVotingProgress,
      getUserVote,
      canVote,
      isDeadlinePassed,
      castVote,
      confirmVote,
      closeVoting,
      viewResolution,
      editResolution,
      submitForVoting,
      confirmSubmitForVoting,
      duplicateResolution,
      shareResolution,
      viewDocuments,
      deleteResolution,
      confirmDelete,
      getResolutionsByStatus,
      loadResolutions
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables.scss";

.resolutions-page {
  .text-medium-emphasis {
    opacity: 0.7;
  }

  .text-primary {
    color: rgb(var(--v-theme-primary)) !important;
  }

  .resolutions-grid {
    .resolution-card {
      transition: transform 0.2s, box-shadow 0.2s;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
      }

      .resolution-description {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .resolution-meta {
        border-top: 1px solid rgba(0, 0, 0, 0.08);
        border-bottom: 1px solid rgba(0, 0, 0, 0.08);
        padding: 12px 0;
      }

      .voting-section {
        background-color: rgba(var(--v-theme-surface), 0.5);
        border-radius: 8px;
        padding: 16px;
        margin: 16px 0;

        .vote-breakdown {
          background-color: rgba(0, 0, 0, 0.02);
          border-radius: 6px;
          padding: 12px;
        }

        .voting-actions {
          .v-btn {
            min-width: 80px;
          }
        }
      }

      .resolution-actions {
        border-top: 1px solid rgba(0, 0, 0, 0.08);
        padding-top: 16px;
      }
    }
  }

  .empty-state {
    margin: 48px 0;
  }

  .stats-section {
    .v-card {
      transition: transform 0.2s;

      &:hover {
        transform: scale(1.02);
      }
    }
  }

  .activity-item {
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 8px;
    margin-bottom: 8px;
  }
}
</style>